import { Badge, Field, SearchCombobox, TextInput } from '@zarya/ui';
import { t } from '@app/i18n';
import {
  defaultText,
  fieldHelp,
  fieldLabel,
  optionLabel,
  type InfraField as CampoInfra,
  type ValueField,
} from './InfraModel';
import s from './Infra.module.css';

/**
 * A frase de cada origem do valor. `spire` (aim-jrn7) é o `trust_domain` lido do SPIRE do cluster: sem ele na tabela,
 * a tela dizia «padrão» para um valor que o cluster decide.
 */
const ORIGIN_KEY: Record<CampoInfra['origin_value'], string> = {
  ambiente: 'infrastructure.field.origin.environment',
  default: 'infrastructure.field.origin.default',
  spire: 'infrastructure.field.origin.spire',
};

export interface InfraFieldProps {
  campo: CampoInfra;
  valor: ValueField;
  onChange: (valor: ValueField) => void;
  /** `false` para approver/viewer, e para `bind_host`/`bind_port` (FR-018). */
  editavel: boolean;
  /** Mensagem do 422 apontando este campo (FR-127). */
  erro?: string | null;
}

/**
 * Um campo da tela de Infraestrutura, **gerado** do que o middleware publica
 * (FR-018): rótulo, nome técnico `AIG_…`, ajuda ⓘ, obrigatoriedade, origem do
 * valor e o controle certo para o `tipo`. A tela não tem lista própria de campos —
 * se o middleware publicar um campo novo amanhã, ele aparece aqui sem release.
 */
export function InfraField({ campo, valor, onChange, editavel, erro }: InfraFieldProps) {
  const canEdit = editavel && campo.editable;
  // Catálogo pela `key`; sem chave e sem rótulo publicado, o nome técnico é o rótulo.
  const rotulo = fieldLabel(campo);
  const ajuda = fieldHelp(campo);

  return (
    <Field
      label={rotulo}
      span={campo.kind === 'lista' ? 12 : 6}
      required={campo.required}
      error={erro}
      {...(ajuda ? { tip: ajuda } : {})}
      help={
        <span className={s.ajudaCampo}>
          {/* Sem rótulo publicado, o nome técnico já **é** o rótulo do campo —
              repeti-lo aqui seria só ruído. */}
          {rotulo !== campo.name ? <code className={s.nomeTecnico}>{campo.name}</code> : null}
          {campo.sensitive ? (
            <span>
              {t(
                campo.defined
                  ? 'infrastructure.field.sensitive.defined'
                  : 'infrastructure.field.sensitive.undefined',
              )}
            </span>
          ) : (
            <span>
              {t('infrastructure.field.origin', {
                origin: t(ORIGIN_KEY[campo.origin_value]),
              })}
              {campo.editable ? '' : t('infrastructure.field.readOnly')}
            </span>
          )}
        </span>
      }
    >
      {(props) => {
        if (campo.kind === 'booleano') {
          return (
            <label className={s.check}>
              <input
                id={props.id}
                type="checkbox"
                checked={valor === true}
                disabled={!canEdit}
                onChange={(e) => onChange(e.target.checked)}
              />
              {t(valor === true ? 'infrastructure.field.on' : 'infrastructure.field.off')}
            </label>
          );
        }

        if (campo.kind === 'opcao') {
          return (
            <SearchCombobox
              id={props.id}
              label={rotulo}
              options={(campo.options ?? []).map((opcao) => {
                // Com rótulo do catálogo, a opção é texto para ler; sem ele, é o
                // valor cru do contrato e sai em `mono`, como antes (§5.3).
                const rotuloDaOpcao = optionLabel(campo, opcao);
                return rotuloDaOpcao
                  ? { value: opcao, label: rotuloDaOpcao }
                  : { value: opcao, label: opcao, mono: true };
              })}
              value={typeof valor === 'string' && valor ? valor : null}
              onChange={(escolha) => onChange(escolha ?? '')}
              disabled={!canEdit}
              {...(props['aria-describedby']
                ? { 'aria-describedby': props['aria-describedby'] }
                : {})}
              {...(props['aria-invalid'] ? { 'aria-invalid': props['aria-invalid'] } : {})}
              {...(props['aria-required'] ? { 'aria-required': props['aria-required'] } : {})}
            />
          );
        }

        // `texto`, `lista` e sensível caem no input; a lista é separada por vírgula.
        return (
          <TextInput
            {...props}
            mono
            type={campo.sensitive ? 'password' : 'text'}
            autoComplete={campo.sensitive ? 'new-password' : 'off'}
            value={typeof valor === 'boolean' ? '' : valor}
            disabled={!canEdit}
            placeholder={
              campo.sensitive && campo.defined
                ? '••••••••'
                : campo.kind === 'lista'
                  ? t('infrastructure.field.listPlaceholder')
                  : defaultText(campo.default)
            }
            onChange={(e) => onChange(e.target.value)}
          />
        );
      }}
    </Field>
  );
}

/** Badge `pronto` / `faltam {n}` da aba (FR-019). */
export function BadgeReadiness({ pronto, faltam }: { pronto: boolean; faltam: number }) {
  return pronto ? (
    <Badge tone="success">{t('infrastructure.readiness.ready')}</Badge>
  ) : (
    <Badge tone="warning">{t('infrastructure.readiness.missing', { n: faltam })}</Badge>
  );
}
